import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithTokenHandling } from '../baseQuery';
import { GET_PROVIDER_LISTS } from './constants';
import { buildSearchParamsAuto } from '../utils';

interface GetProviderListMembersParams {
  listId: string;
  queryParams?: string;
  data?: any;
}

interface CreateListParams {
  listName: string;
}

interface UpdateListParams {
  listId: string;
  listName?: string;
  ownerId?: string;
  sharedWith?: string[];
}

interface DeleteListParams {
  listId: string;
}

interface DeleteListItemParams {
  listId: string;
  itemId: string;
}

interface GetListByIdParams {
  listId: string;
}

interface UploadListParams {
  data: FormData;
}

interface ListItem {
  provider_id: string;
  type: string;
}

interface AddItemsToListParams {
  listId: string;
  items: ListItem[];
}

interface ExportProviderListMembersParams {
  listId: string;
  queryParams: string;
  data: any;
  fileFormat: string;
  columns: string[];
  fileName: string;
}

// Base API for all lists endpoints
export const listsApi = createApi({
  reducerPath: 'lists',
  baseQuery: baseQueryWithTokenHandling,
  tagTypes: [GET_PROVIDER_LISTS],
  endpoints: (builder) => ({
    getLists: builder.query<MedScout.PaginatedResponse & { results: MedScout.List[] }, Record<string, any> | void>({
      query: (params = {}) => {
        const queryParams = buildSearchParamsAuto({
          pageSize: 1500, // TODO: remove this once we have a better way to handle pagination
          sort: 'name',
          order: 'asc',
          ...params,
        });
        return {
          url: `v1/provider-lists/lists/?${queryParams}`,
          method: 'GET',
        };
      },
      providesTags: () => [{ type: GET_PROVIDER_LISTS, id: 'LIST' }],
    }),
    getListById: builder.query<MedScout.List, GetListByIdParams>({
      query: ({ listId }) => ({
        url: `v1/provider-lists/lists/${listId}/`,
        method: 'GET',
      }),
      providesTags: (result, error, { listId }) => [
        { type: GET_PROVIDER_LISTS, id: listId },
      ],
    }),
    getProviderListMembers: builder.mutation<MedScout.PaginatedResponse & { results: any[] }, GetProviderListMembersParams>(
      {
        query: ({ listId, queryParams = '', data }) => ({
          url: `v1/provider-lists/lists/${listId}/members/${queryParams}`,
          method: 'POST',
          body: data,
        }),
      }
    ),
    getProviderListMembersQuery: builder.query<
      MedScout.PaginatedResponse & { results: any[] },
      GetProviderListMembersParams
    >({
      query: ({ listId, queryParams = '', data }) => ({
        url: `v1/provider-lists/lists/${listId}/members/${queryParams}`,
        method: 'POST',
        body: data,
      }),
      providesTags: (result, error, { listId }) => [
        { type: GET_PROVIDER_LISTS, id: `MEMBERS_${listId}` },
      ],
    }),
    createList: builder.mutation<MedScout.List, CreateListParams>({
      query: ({ listName }) => ({
        url: `v1/provider-lists/lists/`,
        method: 'POST',
        body: {
          name: listName,
        },
      }),
      invalidatesTags: [{ type: GET_PROVIDER_LISTS, id: 'LIST' }],
    }),
    updateList: builder.mutation<MedScout.List, UpdateListParams>({
      query: ({ listId, listName, ownerId, sharedWith }) => {
        const newData: Record<string, any> = {};
        if (listName) newData['name'] = listName;
        if (ownerId) newData['owner_id'] = ownerId;
        if (sharedWith) newData['shared_user_ids'] = sharedWith;

        return {
          url: `v1/provider-lists/lists/${listId}/`,
          method: 'PATCH',
          body: newData,
        };
      },
      invalidatesTags: [{ type: GET_PROVIDER_LISTS, id: 'LIST' }],
    }),
    deleteList: builder.mutation<void, DeleteListParams>({
      query: ({ listId }) => ({
        url: `v1/provider-lists/lists/${listId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { listId }) => [
        { type: GET_PROVIDER_LISTS, id: 'LIST' },
        { type: GET_PROVIDER_LISTS, id: listId },
        { type: GET_PROVIDER_LISTS, id: `MEMBERS_${listId}` },
      ],
    }),
    deleteListItem: builder.mutation<void, DeleteListItemParams>({
      query: ({ listId, itemId }) => ({
        url: `v1/provider-lists/lists/${listId}/items/${itemId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { listId }) => [
        { type: GET_PROVIDER_LISTS, id: 'LIST' },
        { type: GET_PROVIDER_LISTS, id: listId },
        { type: GET_PROVIDER_LISTS, id: `MEMBERS_${listId}` },
      ],
    }),
    uploadList: builder.mutation<MedScout.List, UploadListParams>({
      query: ({ data }) => ({
        url: `v1/provider-lists/upload/`,
        method: 'POST',
        body: data,
        // DO NOT set Content-Type header for FormData
        // Browser will automatically set it with the correct boundary
      }),
      invalidatesTags: [{ type: GET_PROVIDER_LISTS, id: 'LIST' }],
    }),
    addItemsToList: builder.mutation<void, AddItemsToListParams>({
      query: ({ listId, items }) => ({
        url: `v1/provider-lists/lists/${listId}/items/`,
        method: 'POST',
        body: items,
      }),
      invalidatesTags: (result, error, { listId }) => [
        { type: GET_PROVIDER_LISTS, id: 'LIST' },
        { type: GET_PROVIDER_LISTS, id: listId },
        { type: GET_PROVIDER_LISTS, id: `MEMBERS_${listId}` },
      ],
    }),
    exportProviderListMembers: builder.mutation<
      void,
      ExportProviderListMembersParams
    >({
      query: ({ listId, queryParams, data, fileFormat, columns, fileName }) => {
        const url = `https://api.medscout.io/api/v1/provider-lists/lists/${listId}/members/${queryParams}`;

        return {
          url: 'v1/admin/export/',
          method: 'POST',
          body: {
            method: 'POST',
            url,
            file_format: fileFormat,
            file_name: fileName,
            columns,
            body: data,
          },
        };
      },
    }),
  }),
});

export const {
  useGetListsQuery,
  useGetListByIdQuery,
  useGetProviderListMembersMutation,
  useGetProviderListMembersQueryQuery: useGetProviderListMembersQuery,
  useCreateListMutation,
  useUpdateListMutation,
  useDeleteListMutation,
  useDeleteListItemMutation,
  useUploadListMutation,
  useAddItemsToListMutation,
  useExportProviderListMembersMutation,
} = listsApi;
